// GitHub sync trigger
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { AuthProvider } from "@/contexts/AuthContext";
import LandingPage from "./pages/LandingPage";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminProtectedRoute } from "@/components/auth/AdminProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { SkipToContent } from "@/components/SkipToContent";
import { usePageTracking } from "@/hooks/usePageTracking";
import { ErrorFallback } from "@/components/ErrorFallback";
import { Sentry } from "@/lib/sentry";

// Route-level code splitting - protected and admin routes are lazy.
const Directory = lazy(() => import("./pages/Directory"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const KnowledgeHub = lazy(() => import("./pages/KnowledgeHub"));
const Events = lazy(() => import("./pages/Events"));
const MyConnections = lazy(() => import("./pages/MyConnections"));
const Notifications = lazy(() => import("./pages/Notifications"));
const ConnectionFeedback = lazy(() => import("./pages/ConnectionFeedback"));
const Team = lazy(() => import("./pages/Team"));
const About = lazy(() => import("./pages/About"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Admin is gated behind AdminProtectedRoute - always worth splitting.
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AdminOverview = lazy(() => import("./components/admin/sections/Overview"));
const AdminMembers = lazy(() => import("./components/admin/sections/Members"));
const AdminConnections = lazy(() => import("./components/admin/sections/Connections"));
const AdminFeedback = lazy(() => import("./components/admin/sections/Feedback"));
const AdminKnowledgeHub = lazy(() => import("./components/admin/sections/KnowledgeHubAdmin"));
const AdminEvents = lazy(() => import("./components/admin/sections/EventsAdmin"));
const AdminTeam = lazy(() => import("./components/admin/sections/TeamAdmin"));
const AdminApplications = lazy(() => import("./components/admin/sections/ApplicationsAdmin"));
const AdminAuditLog = lazy(() => import("./components/admin/sections/AuditLog"));
const AdminDigest = lazy(() => import("./components/admin/sections/Digest"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const AdminPreviewMember = lazy(() => import("./pages/AdminPreviewMember"));
const Status = lazy(() => import("./pages/Status"));
const Article = lazy(() => import("./pages/Article"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const Welcome = lazy(() => import("./pages/Welcome"));
const Settings = lazy(() => import("./pages/Settings"));

const RouteFallback = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const queryClient = new QueryClient();

const PageTracker = () => {
  usePageTracking();
  return null;
};

const AuthRedirect = () => {
  const [params] = useSearchParams();
  const target = params.get("tab") === "login" ? "/login" : "/signup";
  return <Navigate to={target} replace />;
};

const JoinRedirect = () => <Navigate to="/signup" replace />;

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        <Suspense fallback={<RouteFallback />}>
          <Routes location={location}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/directory" element={<ProtectedRoute><Directory /></ProtectedRoute>} />
            <Route path="/signup" element={<Auth />} />
            <Route path="/login" element={<Auth />} />
            <Route path="/join" element={<JoinRedirect />} />
            <Route path="/auth" element={<AuthRedirect />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/team" element={<Team />} />
            <Route path="/about" element={<About />} />
            <Route path="/status" element={<Status />} />
            <Route path="/article/ambidextro-organisations" element={<Article />} />
            <Route path="/legal/fbn-international-terms-of-use" element={<LegalPage slug="fbn-international-terms-of-use" />} />
            <Route path="/legal/fbn-international-privacy-notice" element={<LegalPage slug="fbn-international-privacy-notice" />} />
            <Route path="/legal/supplementary-terms-of-use" element={<LegalPage slug="supplementary-terms-of-use" />} />
            <Route path="/legal/supplementary-privacy-notice" element={<LegalPage slug="supplementary-privacy-notice" />} />
            <Route path="/legal/code-of-conduct" element={<LegalPage slug="code-of-conduct" />} />
            <Route path="/knowledge-hub" element={<KnowledgeHub />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
            <Route path="/my-connections" element={<ProtectedRoute><MyConnections /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/connection-feedback/:connectionId" element={<ProtectedRoute><ConnectionFeedback /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminProtectedRoute><AdminPanel /></AdminProtectedRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="overview" element={<AdminOverview />} />
              <Route path="members" element={<AdminMembers />} />
              <Route path="connections" element={<AdminConnections />} />
              <Route path="feedback" element={<AdminFeedback />} />
              <Route path="knowledge-hub" element={<AdminKnowledgeHub />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="applications" element={<AdminApplications />} />
              <Route path="audit" element={<AdminAuditLog />} />
              <Route path="digest" element={<AdminDigest />} />
              <Route path="preview/:userId" element={<AdminPreviewMember />} />
            </Route>
            <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

const App = () => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
  >
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <MotionConfig reducedMotion="user">
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <SkipToContent />
              <ScrollToTop />
              <PageTracker />
              <AnimatedRoutes />
            </BrowserRouter>
          </MotionConfig>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
